



import Vue from 'vue';
import axios from 'axios';
import OSS from 'ali-oss';
import { Loading } from 'element-ui';
import local from './local.js';

const v = new Vue();

// 默认配置（移除硬编码 AK/SK，改走后端 STS 临时凭证）
const DEFAULT_CONFIG = {
  region: 'oss-cn-guangzhou',
  bucket: 'xiaocanguan',
  customDomain: 'https://oss.jinding.work',
  defaultChunkSize: 5 * 1024 * 1024, // 默认分片大小5MB
  defaultMultipartThreshold: 100 * 1024 * 1024, // 默认触发分片上传的文件大小100MB（ali-oss SDK 会自动判断是否分片）
  stsEndpoint: '/v1/upload/sts', // 后端获取 STS 凭证的接口
  stsCacheMs: 25 * 60 * 1000, // STS 凭证本地缓存 25 分钟（后端默认有效期 30 分钟，提前 5 分钟过期避免边界）
  stsMaxRetryOn403: 1, // STS 过期后最多重试 1 次
  debug: false
};

// 拼接后端 API baseUrl（与 src/api/http.js 保持一致）
const filterUrl = url => {
  const baseUrl = '/api';
  if (url && url.startsWith('http')) return url;
  return baseUrl + url;
};

class OSSUploader {
  constructor(config = {}) {
    this.config = Object.assign({}, DEFAULT_CONFIG, Object.assign({}, config));
    this.isUploading = false;
    // STS 凭证缓存：{ [dir]: { credentials, expireAt, client } }
    this._stsCache = {};
    // 当前上传的 AbortController（用于取消上传）
    this._abortController = null;
  }

  // 获取文件类型
  _getFileType(file) {
    const typeMap = {
      // 图片格式
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/bmp': 'bmp',
      'image/svg+xml': 'svg',
      'image/tiff': 'tiff',
      'image/x-icon': 'ico',

      // 音频格式
      'audio/mp3': 'mp3',
      'audio/mpeg': 'mp3',
      'audio/wav': 'wav',
      'audio/ogg': 'ogg',
      'audio/m4a': 'm4a',
      'audio/aac': 'aac',
      'audio/flac': 'flac',

      // 视频格式
      'video/mp4': 'mp4',
      'video/mpeg': 'mpeg',
      'video/avi': 'avi',
      'video/mov': 'mov',
      'video/wmv': 'wmv',
      'video/flv': 'flv',
      'video/webm': 'webm',
      'video/3gpp': '3gp',
      'video/quicktime': 'mov',
      'video/x-msvideo': 'avi',

      // 文档格式
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/vnd.ms-powerpoint': 'ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
      'text/plain': 'txt',
      'text/html': 'html',
      'text/css': 'css',
      'text/javascript': 'js',
      'application/json': 'json',
      'application/xml': 'xml',

      // 压缩格式
      'application/zip': 'zip',
      'application/x-rar-compressed': 'rar',
      'application/x-7z-compressed': '7z',
      'application/gzip': 'gz',
      'application/x-tar': 'tar',

      // 其他常见格式
      'application/octet-stream': 'bin',
      'application/x-shockwave-flash': 'swf',
      'font/ttf': 'ttf',
      'font/woff': 'woff',
      'font/woff2': 'woff2',
      'application/vnd.android.package-archive': 'apk',
      'application/x-msdownload': 'exe',
      'application/x-apple-diskimage': 'dmg'
    };
    return typeMap[file.type] || 'file';
  }

  // 根据URL获取文件类型
  _getFileTypeFromUrl(url) {
    const extension = url.split('.').pop().split('?')[0].split('#')[0].toLowerCase();
    const typeMap = {
      // 图片格式
      'jpg': 'jpg',
      'jpeg': 'jpg',
      'png': 'png',
      'gif': 'gif',
      'webp': 'webp',
      'bmp': 'bmp',
      'svg': 'svg',
      'ico': 'ico',
      'tiff': 'tiff',
      'tif': 'tiff',

      // 音频格式
      'mp3': 'mp3',
      'wav': 'wav',
      'ogg': 'ogg',
      'm4a': 'm4a',
      'aac': 'aac',
      'flac': 'flac',
      'wma': 'wma',

      // 视频格式
      'mp4': 'mp4',
      'avi': 'avi',
      'mov': 'mov',
      'wmv': 'wmv',
      'flv': 'flv',
      'webm': 'webm',
      'mkv': 'mkv',
      'm3u8': 'm3u8',
      'ts': 'ts',
      '3gp': '3gp',
      'mpeg': 'mpeg',
      'mpg': 'mpg',

      // 文档格式
      'pdf': 'pdf',
      'doc': 'doc',
      'docx': 'docx',
      'xls': 'xls',
      'xlsx': 'xlsx',
      'ppt': 'ppt',
      'pptx': 'pptx',
      'txt': 'txt',
      'md': 'md',
      'html': 'html',
      'htm': 'htm',
      'css': 'css',
      'js': 'js',
      'json': 'json',
      'xml': 'xml',

      // 压缩格式
      'zip': 'zip',
      'rar': 'rar',
      '7z': '7z',
      'gz': 'gz',
      'tar': 'tar',

      // 其他格式
      'apk': 'apk',
      'exe': 'exe',
      'dmg': 'dmg',
      'ipa': 'ipa',
      'iso': 'iso',
      'bin': 'bin',
      'dat': 'dat',
      'log': 'log',
      'ini': 'ini',
      'cfg': 'cfg',
      'conf': 'conf'
    };
    return typeMap[extension] || 'file';
  }

  // 根据文件类型获取场景目录
  _getSceneByFileType(fileType) {
    const sceneMap = {
      // 图片
      'jpg': 'images',
      'png': 'images',
      'gif': 'images',
      'webp': 'images',
      'bmp': 'images',
      'svg': 'images',
      'ico': 'images',
      'tiff': 'images',

      // 音频
      'mp3': 'audios',
      'wav': 'audios',
      'ogg': 'audios',
      'm4a': 'audios',
      'aac': 'audios',
      'flac': 'audios',

      // 视频
      'mp4': 'videos',
      'avi': 'videos',
      'mov': 'videos',
      'wmv': 'videos',
      'flv': 'videos',
      'webm': 'videos',
      'mkv': 'videos',

      // 文档
      'pdf': 'documents',
      'doc': 'documents',
      'docx': 'documents',
      'xls': 'documents',
      'xlsx': 'documents',
      'ppt': 'documents',
      'pptx': 'documents',
      'txt': 'documents',

      // 其他
      'default': 'files'
    };
    return sceneMap[fileType] || sceneMap.default;
  }

  // 从URL获取文件名
  _getFilenameFromUrl(url) {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop();
      return filename || `${Date.now()}_downloaded`;
    } catch (e) {
      return `${Date.now()}_downloaded`;
    }
  }

  // 从URL获取MIME类型
  async _getMimeTypeFromUrl(url) {
    try {
      // 首先尝试从扩展名推断
      const extension = url.split('.').pop().split('?')[0].split('#')[0].toLowerCase();
      const inferredType = this._getContentTypeByExtension(extension);

      // 然后尝试HEAD请求获取准确的Content-Type
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      try {
        const response = await fetch(url, {
          method: 'HEAD',
          mode: 'no-cors', // 使用no-cors避免CORS问题
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        // 注意：no-cors模式下无法读取响应头
        // 所以回退到扩展名推断的类型
        return inferredType;
      } catch (headError) {
        clearTimeout(timeoutId);
        // HEAD请求失败，使用扩展名推断的类型
        return inferredType;
      }
    } catch (error) {
      console.warn('无法获取URL的MIME类型，使用默认值:', error);
      return 'application/octet-stream';
    }
  }

  // 生成日期目录格式
  _getDatePath() {
    const date = new Date();
    return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  }

  // 生成完整文件路径
  // 修改文件路径生成方法，支持自动场景分类
  _generateFilePath(file, filename, scene, fileType) {
    const datePath = this._getDatePath();
    const originalName = file ? file.name.replace(/\s+/g, '_') : '';
    const finalScene = scene || this._getSceneByFileType(fileType);
    return `${finalScene}/${fileType}/${datePath}/${filename || `${Date.now()}_${originalName}`}`;
  }

  // 根据URL生成文件路径
  _generateFilePathFromUrl(url, filename, scene) {
    const datePath = this._getDatePath();
    const fileType = this._getFileTypeFromUrl(url);
    const originalName = filename || this._getFilenameFromUrl(url);
    const finalScene = scene || this._getSceneByFileType(fileType);
    return `${finalScene}/${fileType}/${datePath}/${originalName}`;
  }

  // 根据文件类型设置默认的Content-Type
  _getContentTypeByExtension(extension) {
    const contentTypeMap = {
      // 图片
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'bmp': 'image/bmp',
      'svg': 'image/svg+xml',
      'ico': 'image/x-icon',
      'tiff': 'image/tiff',

      // 音频
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
      'm4a': 'audio/mp4',
      'aac': 'audio/aac',
      'flac': 'audio/flac',

      // 视频
      'mp4': 'video/mp4',
      'avi': 'video/x-msvideo',
      'mov': 'video/quicktime',
      'wmv': 'video/x-ms-wmv',
      'flv': 'video/x-flv',
      'webm': 'video/webm',

      // 文档
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'txt': 'text/plain',
      'html': 'text/html',
      'css': 'text/css',
      'js': 'text/javascript',
      'json': 'application/json',

      // 默认
      'default': 'application/octet-stream'
    };
    return contentTypeMap[extension] || contentTypeMap.default;
  }

  // ============ STS 临时凭证相关（私有 bucket 改造核心） ============

  // 调后端接口拿 STS 临时凭证
  // 后端返回结构假设：{ status:1, data:{ region, bucket, AccessKeyId, AccessKeySecret, SecurityToken, Expiration, dir } }
  async _getStsCredentials(dir) {
    const token = local.get('token');
    const brand = local.get('brand');
    const response = await axios.post(filterUrl(this.config.stsEndpoint), { dir }, {
      headers: {
        'Authorization': token,
        'Brand': brand,
        'Platform': 'pc',
        'Content-Type': 'application/json'
      },
      timeout: 15000
    });
    const body = response && response.data;
    if (body && body.status === 1 && body.data) {
      return body.data;
    }
    throw new Error((body && body.message) || '获取 STS 凭证失败');
  }

  // 获取一个有效的 STS client（带缓存，提前 5 分钟过期）
  // dir 用于按目录前缀缓存（不同 dir 对应的 RAM Policy 可能不同）
  async _getValidStsClient(dir) {
    const cacheKey = dir || '_default_';
    const now = Date.now();
    const cached = this._stsCache[cacheKey];

    if (cached && cached.expireAt > now) {
      return cached.client;
    }

    // 取新凭证
    const creds = await this._getStsCredentials(dir);

    // 兼容后端可能返回的字段名（AccessKeyId vs accessKeyId 等）
    const region = creds.region || this.config.region;
    const bucket = creds.bucket || this.config.bucket;
    const accessKeyId = creds.AccessKeyId || creds.accessKeyId;
    const accessKeySecret = creds.AccessKeySecret || creds.accessKeySecret;
    const stsToken = creds.SecurityToken || creds.stsToken || creds.securityToken;

    if (!accessKeyId || !accessKeySecret || !stsToken) {
      throw new Error('STS 凭证字段缺失，请后端确认返回 AccessKeyId/AccessKeySecret/SecurityToken');
    }

    const client = new OSS({
      region,
      bucket,
      accessKeyId,
      accessKeySecret,
      stsToken,
      secure: true
    });

    // 计算过期时间：优先用后端返回的 Expiration，否则用配置的缓存时间
    let expireAt = now + this.config.stsCacheMs;
    if (creds.Expiration) {
      const exp = new Date(creds.Expiration).getTime();
      if (!isNaN(exp)) {
        // 提前 5 分钟过期，避免边界
        expireAt = exp - 5 * 60 * 1000;
      }
    }

    this._stsCache[cacheKey] = { client, expireAt, credentials: creds };
    return client;
  }

  // 清空 STS 缓存（用于 403 后强制重新取凭证）
  _invalidateStsCache(dir) {
    if (dir) {
      delete this._stsCache[dir];
      delete this._stsCache._default_;
    } else {
      this._stsCache = {};
    }
  }

  // 判断错误是否由 STS 过期/失效引起（用于触发重试）
  _isStsExpiredError(error) {
    if (!error) return false;
    // ali-oss SDK 抛出的错误会带 status / code 字段
    const status = error.status || (error.response && error.response.status);
    const code = error.code || (error.response && (error.response.code || (error.response.data && error.response.data.Code)));
    const message = error.message || '';
    // 常见过期/失效错误码：InvalidAccessKeyId, SecurityToken.Expired, RequestExpired, InvalidSecurityToken
    if (status === 403 || status === 401) return true;
    if (code && /InvalidAccessKeyId|SecurityToken|RequestExpired|InvalidSecurityToken|AccessDenied/i.test(code)) return true;
    if (/security token|expired|invalid access key|access denied/i.test(message)) return true;
    return false;
  }

  // ============ 核心上传：ali-oss SDK + STS + 403 重试 ============
  // 内部使用，不直接对外
  async _uploadWithSTS({ file, key, dir, customParams = {}, chunkSize, onProgress, retryCount = 0 }) {
    const client = await this._getValidStsClient(dir);
    // 构造 OSS headers（私有 bucket 下过滤 x-oss-object-acl，避免权限报错）
    const headers = {};
    Object.keys(customParams).forEach(k => {
      const v = customParams[k];
      if (v === undefined || v === null) return;
      const lowerKey = String(k).toLowerCase();
      // 跳过 x-oss-object-acl（私有 bucket 改造后不允许前端指定对象 ACL）
      if (lowerKey === 'x-oss-object-acl') return;
      // 大写首字母：content-disposition -> Content-Disposition
      const formKey = lowerKey === 'content-disposition' ? 'Content-Disposition' : k;
      headers[formKey] = v;
    });

    // 取消控制器（保存到 this._abortController，cancelUpload 时使用）
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    try {
      const result = await client.multipartUpload(key, file, {
        partSize: chunkSize || this.config.defaultChunkSize,
        parallel: 4,
        signal,
        progress: (p, checkpoint) => {
          // ali-oss 进度回调 p 是 0~1 的小数
          const percent = Math.max(0, Math.min(100, p * 100));
          if (onProgress) {
            onProgress({
              loaded: Math.floor((percent / 100) * (file.size || 0)),
              total: file.size || 0,
              percent,
              stage: 'uploading',
              checkpoint
            });
          }
        },
        headers
      });
      return result;
    } catch (error) {
      // 用户主动取消
      if (error && (error.name === 'cancel' || error.name === 'AbortError' || (signal && signal.aborted))) {
        throw new Error('上传已取消');
      }
      // STS 过期 → 清缓存 + 重试一次
      if (retryCount < this.config.stsMaxRetryOn403 && this._isStsExpiredError(error)) {
        console.warn('STS 凭证已过期，重新获取后重试上传', error.message || error.code);
        this._invalidateStsCache(dir);
        return this._uploadWithSTS({ file, key, dir, customParams, chunkSize, onProgress, retryCount: retryCount + 1 });
      }
      throw error;
    }
  }

  // 修改copyFromUrl方法，添加文件类型支持
  async copyFromUrl({
    sourceUrl,
    filename = '',
    scene = '',
    fileType = '',
    customParams = {},
    onProgress = () => {},
    onComplete = () => {}
  }) {
    if (!sourceUrl) {
      throw new Error('请输入源文件URL');
    }

    // 验证URL格式
    try {
      new URL(sourceUrl);
    } catch (e) {
      throw new Error('URL格式不正确');
    }

    const loading = Loading.service({
      lock: true,
      text: '正在初始化文件转存... 0.0%',
      background: 'rgba(0, 0, 0, 0.7)'
    });

    try {
      this.isUploading = true;

      // 如果手动指定了文件类型，覆盖自动检测
      let finalFileType = fileType;
      if (!finalFileType) {
        finalFileType = this._getFileTypeFromUrl(sourceUrl);
      }

      // 生成目标文件路径
      const destinationKey = this._generateFilePathFromUrl(sourceUrl, filename, scene || this._getSceneByFileType(finalFileType));

      // 使用安全的进度回调包装
      const safeOnProgress = (data) => {
        this._safeProgressCallback(onProgress, data);
      };

      this._updateProgressLoading(loading, '正在处理URL', 5);

      let fileUrl;

      // 特别处理萤石云URL
      if (sourceUrl.includes('ys7.com')) {
        this._updateProgressLoading(loading, '检测到萤石云URL，使用重定向跟踪方法', 10);

        try {
          // 使用XMLHttpRequest跟踪重定向
          fileUrl = await this._handleYs7Image(sourceUrl, destinationKey, customParams, safeOnProgress, loading);
        } catch (ys7Error) {
          console.error('萤石云处理失败:', ys7Error);

          // 提示用户手动处理
          throw new Error(`萤石云图片处理失败: ${ys7Error.message}\n\n由于安全限制，建议：\n1. 手动保存图片\n2. 使用本地上传功能\n3. 联系开发配置后端代理`);
        }
      } else {
        // 其他URL使用常规方法
        try {
          fileUrl = await this._copyViaFetch(sourceUrl, destinationKey, customParams, safeOnProgress);
        } catch (error) {
          console.warn('常规方法失败:', error.message);

          if (error.message.includes('CORS') || error.message.includes('跨域') ||
              error.message.includes('Failed to fetch')) {

            this._updateProgressLoading(loading, '无法绕过跨域限制，尝试备选方案', 20);
            fileUrl = await this._downloadWithCorsBypass(sourceUrl, destinationKey, customParams, safeOnProgress, loading);
          } else {
            throw error;
          }
        }
      }

      // 私有 bucket：把 url 字段也升级为 String 对象（主值=裸URL入库用，挂 .previewUrl 用于即时预览）
      // - 老代码 fileUrl.url 直接传给后端 → toString 自动转裸 URL，行为不变
      // - 老代码 fileUrl.url 绑到 :src → 私有 bucket 下会 403，需要改用 fileUrl.url.previewUrl 或 fileUrl.previewUrl
      let previewUrl = fileUrl;
      try {
        previewUrl = await this._getSignedUrl(fileUrl);
      } catch (e) {
        console.warn('获取预览签名URL失败，降级使用裸URL:', e.message);
      }

      const urlObj = new String(fileUrl);
      urlObj.previewUrl = previewUrl;
      urlObj.url = fileUrl;

      // 双参 onComplete：第一个参数是 String 对象（兼容老代码 toString=裸URL），第二个是签名URL用于即时显示
      onComplete(urlObj, previewUrl);
      loading.close();

      return {
        url: urlObj,
        previewUrl,
        fileType: finalFileType,
        contentType: await this._getMimeTypeFromUrl(sourceUrl)
      };
    } catch (error) {
      loading.close();
      console.error('从URL复制文件失败:', error);

      let errorMessage = error.message;
      let solution = '';

      if (sourceUrl.includes('ys7.com')) {
        errorMessage = '萤石云图片处理失败';
        solution = '萤石云有严格的安全限制，无法直接下载。\n' +
                  '请按以下步骤操作：\n' +
                  '1. 在浏览器中打开图片链接\n' +
                  '2. 右键点击图片，选择"另存为"\n' +
                  '3. 使用"本地上传"功能上传保存的图片';
      } else if (error.message.includes('CORS') || error.message.includes('跨域')) {
        errorMessage = '跨域访问被拒绝';
        solution = '由于浏览器的安全限制，无法直接下载此资源。\n' +
                  '建议：\n' +
                  '1. 手动下载文件后上传\n' +
                  '2. 联系资源提供方启用CORS\n' +
                  '3. 配置后端代理服务';
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        errorMessage = '访问被拒绝(403)';
        solution = '此资源可能需要权限验证';
      } else if (error.message.includes('timeout')) {
        errorMessage = '请求超时';
        solution = '网络连接较慢或资源服务器响应慢';
      }

      const fullError = new Error(`文件转存失败: ${errorMessage}`);
      fullError.solution = solution;
      fullError.originalError = error.message;
      fullError.manualSteps = sourceUrl.includes('ys7.com') ?
        '手动操作步骤：\n1. 在新标签页打开链接\n2. 右键保存图片\n3. 使用本地上传功能' :
        '请尝试手动下载后上传';

      throw fullError;
    } finally {
      this.isUploading = false;
    }
  }

  // 添加上传本地文件的方法（兼容旧调用，直接走统一 upload 入口）
  async uploadLocalFile({
    file,
    filename = '',
    scene = 'default',
    customParams = {},
    onProgress = () => {},
    onComplete = () => {}
  }) {
    if (!file) {
      throw new Error('请选择文件');
    }
    return this.upload({
      file,
      filename,
      scene,
      customParams,
      onProgress,
      onComplete
    });
  }

  // 添加处理重定向的方法
  async _followRedirects(sourceUrl, maxRedirects = 5) {
    let currentUrl = sourceUrl;
    let redirectCount = 0;

    while (redirectCount < maxRedirects) {
      try {
        // 使用XMLHttpRequest来跟踪重定向
        const xhr = new XMLHttpRequest();

        const response = await new Promise((resolve, reject) => {
          xhr.open('GET', currentUrl, true);
          xhr.responseType = 'blob'; // 获取blob响应

          // 设置必要的请求头
          xhr.setRequestHeader('User-Agent', 'Mozilla/5.0');
          xhr.setRequestHeader('Accept', 'image/*,*/*;q=0.8');

          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve({
                status: xhr.status,
                responseURL: xhr.responseURL,
                blob: xhr.response,
                headers: xhr.getAllResponseHeaders()
              });
            } else {
              reject(new Error(`HTTP ${xhr.status}`));
            }
          };

          xhr.onerror = function() {
            reject(new Error('Network error'));
          };

          xhr.ontimeout = function() {
            reject(new Error('Timeout'));
          };

          xhr.timeout = 30000;
          xhr.send();
        });

        // 检查是否是重定向
        if (response.responseURL && response.responseURL !== currentUrl) {
          redirectCount++;
          currentUrl = response.responseURL;
          console.log(`重定向 ${redirectCount}: ${currentUrl}`);
          continue;
        }

        // 如果不是重定向，返回结果
        return {
          finalUrl: currentUrl,
          blob: response.blob,
          headers: response.headers
        };
      } catch (error) {
        throw new Error(`跟踪重定向失败: ${error.message}`);
      }
    }

    throw new Error(`重定向次数超过限制: ${maxRedirects}`);
  }

  // 添加萤石云图片的特殊处理方法
  async _handleYs7Image(sourceUrl, destinationKey, customParams, onProgress, loading) {
    this._updateProgressLoading(loading, '正在处理萤石云图片（跟踪重定向）', 30);

    try {
      // 1. 跟踪重定向获取最终URL和图片
      this._updateProgressLoading(loading, '正在跟踪重定向...', 40);

      const result = await this._followRedirects(sourceUrl);
      console.log('重定向结果:', {
        finalUrl: result.finalUrl,
        blobSize: result.blob.size,
        blobType: result.blob.type
      });

      this._updateProgressLoading(loading, '重定向跟踪完成，准备上传', 60);

      // 2. 从blob创建文件
      const fileName = this._getFilenameFromUrl(result.finalUrl) || 'ys7_image.jpg';
      const file = new File([result.blob], fileName, {
        type: result.blob.type || 'image/jpeg',
        lastModified: Date.now()
      });

      loading.close();

      const uploadLoading = Loading.service({
        lock: true,
        text: '正在上传到OSS... 60.0%',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      // 3. 上传文件
      const uploadResult = await this.upload({
        file,
        filename: fileName,
        scene: destinationKey.split('/')[0],
        customParams: {
          ...customParams,
          'Content-Type': result.blob.type || 'image/jpeg'
        },
        onProgress: (progress) => {
          const overallPercent = 60 + ((progress.percent || 0) * 0.4);
          this._updateProgressLoading(uploadLoading, '正在上传到OSS', overallPercent);
          this._safeProgressCallback(onProgress, {
            loaded: (result.blob.size || 0) + (progress.loaded || 0),
            total: (result.blob.size || 0) + (progress.total || 0),
            percent: Math.min(overallPercent, 100),
            stage: 'uploading'
          });
        }
      });

      this._updateProgressLoading(uploadLoading, '上传完成', 100);
      setTimeout(() => uploadLoading.close(), 500);

      return uploadResult;

    } catch (error) {
      console.error('萤石云重定向处理失败:', error);

      // 如果重定向方法失败，尝试备选方案
      this._updateProgressLoading(loading, '重定向失败，尝试备选方案', 50);
      return await this._handleYs7ImageFallback(sourceUrl, destinationKey, customParams, onProgress, loading);
    }
  }

  // 萤石云备选方案
  async _handleYs7ImageFallback(sourceUrl, destinationKey, customParams, onProgress, loading) {
    this._updateProgressLoading(loading, '使用备选方案处理萤石云图片', 50);

    try {
      // 方法1: 尝试通过iframe处理
      this._updateProgressLoading(loading, '尝试iframe方法', 60);
      const iframeResult = await this._downloadViaIframe(sourceUrl, destinationKey, customParams, onProgress, loading, 60);
      if (iframeResult) return iframeResult;
    } catch (error) {
      console.warn('iframe方法失败:', error.message);
    }

    // 方法2: 尝试通过后端代理
    this._updateProgressLoading(loading, 'iframe失败，尝试后端代理', 70);
    throw new Error('需要后端代理支持，请联系开发人员');
  }

  // 通过iframe下载图片
  async _downloadViaIframe(sourceUrl, destinationKey, customParams, onProgress, loading, startPercent = 0) {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';

      // 设置iframe的sandbox属性
      iframe.sandbox = 'allow-scripts allow-same-origin';

      document.body.appendChild(iframe);

      const timeoutId = setTimeout(() => {
        document.body.removeChild(iframe);
        reject(new Error('iframe加载超时'));
      }, 30000);

      iframe.onload = async () => {
        clearTimeout(timeoutId);

        try {
          this._updateProgressLoading(loading, 'iframe加载完成，提取图片', startPercent + 10);

          // 尝试从iframe中提取图片
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!doc) {
            throw new Error('无法访问iframe内容');
          }

          // 查找图片
          const img = doc.querySelector('img');
          if (!img || !img.src) {
            throw new Error('iframe中未找到图片');
          }

          this._updateProgressLoading(loading, '提取图片成功，准备上传', startPercent + 20);

          // 通过canvas处理图片
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const tempImg = new Image();

          tempImg.onload = async () => {
            canvas.width = tempImg.width;
            canvas.height = tempImg.height;
            ctx.drawImage(tempImg, 0, 0);

            canvas.toBlob(async (blob) => {
              document.body.removeChild(iframe);

              if (!blob) {
                reject(new Error('图片转换失败'));
                return;
              }

              this._updateProgressLoading(loading, '图片转换完成，开始上传', startPercent + 30);

              const fileName = this._getFilenameFromUrl(sourceUrl) || 'ys7_image.png';
              const file = new File([blob], fileName, {
                type: 'image/png',
                lastModified: Date.now()
              });

              loading.close();

              const uploadLoading = Loading.service({
                lock: true,
                text: `正在上传到OSS... ${startPercent + 30}.0%`,
                background: 'rgba(0, 0, 0, 0.7)'
              });

              try {
                const result = await this.upload({
                  file,
                  filename: fileName,
                  scene: destinationKey.split('/')[0],
                  customParams: {
                    ...customParams,
                    'Content-Type': 'image/png'
                  },
                  onProgress: (progress) => {
                    const overallPercent = startPercent + 30 + ((progress.percent || 0) * 0.7);
                    this._updateProgressLoading(uploadLoading, '正在上传到OSS', overallPercent);
                    this._safeProgressCallback(onProgress, {
                      loaded: (blob.size || 0) + (progress.loaded || 0),
                      total: (blob.size || 0) + (progress.total || 0),
                      percent: Math.min(overallPercent, 100),
                      stage: 'uploading'
                    });
                  }
                });

                this._updateProgressLoading(uploadLoading, '上传完成', 100);
                setTimeout(() => uploadLoading.close(), 500);

                resolve(result);
              } catch (uploadError) {
                uploadLoading.close();
                reject(uploadError);
              }
            }, 'image/png');
          };

          tempImg.onerror = () => {
            document.body.removeChild(iframe);
            reject(new Error('图片加载失败'));
          };

          // 设置跨域属性
          tempImg.crossOrigin = 'anonymous';
          tempImg.src = img.src;

        } catch (error) {
          document.body.removeChild(iframe);
          reject(error);
        }
      };

      iframe.onerror = () => {
        clearTimeout(timeoutId);
        document.body.removeChild(iframe);
        reject(new Error('iframe加载错误'));
      };

      // 加载URL
      iframe.src = sourceUrl;
    });
  }


  // 服务器端下载（需要后端支持）
  async _tryServerSideDownload(sourceUrl, destinationKey, customParams, onProgress, loading) {
    // 这里可以调用后端API进行下载
    // 后端可以绕过浏览器的CORS限制
    throw new Error('服务器端下载功能需要后端支持，请联系开发人员配置后端代理服务');
  }

  // 修改Loading服务，添加进度百分比
  _updateProgressLoading(loadingInstance, text, percent = null) {
    if (!loadingInstance) return;

    let loadingText = text;
    if (percent !== null && typeof percent === 'number') {
      const percentStr = percent.toFixed(1);
      loadingText = `${text} ${percentStr}%`;
    }

    loadingInstance.setText(loadingText);
  }

  // 优化Fetch方法，支持大文件
  async _copyViaFetch(sourceUrl, destinationKey, customParams, onProgress) {
    const loading = Loading.service({
      lock: true,
      text: '正在处理文件... 0.0%',
      background: 'rgba(0, 0, 0, 0.7)'
    });

    try {
      // 1. 先获取文件信息
      this._updateProgressLoading(loading, '正在获取文件信息', 5);
      const fileInfo = await this._getRemoteFileInfo(sourceUrl);

      // 如果有CORS错误，使用特殊的下载方法
      if (fileInfo.corsError) {
        this._updateProgressLoading(loading, '检测到跨域限制，使用特殊方法', 10);
        return await this._downloadWithCorsBypass(sourceUrl, destinationKey, customParams, onProgress, loading);
      }

      if (!fileInfo.valid) {
        throw new Error(`无法获取文件信息: ${fileInfo.error}`);
      }

      const totalSize = fileInfo.contentLength;
      const contentType = fileInfo.contentType || await this._getMimeTypeFromUrl(sourceUrl);
      const fileType = this._getFileTypeFromUrl(sourceUrl);

      // 2. 根据不同文件类型选择处理策略
      if (fileInfo.isImage || (contentType && contentType.startsWith('image/'))) {
        return await this._downloadAndUploadImage(sourceUrl, destinationKey, contentType, customParams, onProgress, loading);
      } else if (totalSize > this.config.defaultMultipartThreshold && fileInfo.supportsRange) {
        return await this._downloadWithRange(sourceUrl, destinationKey, totalSize, contentType, customParams, onProgress, loading);
      } else {
        return await this._downloadAndUploadDirect(sourceUrl, destinationKey, contentType, customParams, onProgress, loading, totalSize);
      }
    } catch (error) {
      loading.close();
      throw error;
    }
  }

  // 通过公共CORS代理下载
  async _downloadViaPublicProxy(sourceUrl, destinationKey, customParams, onProgress, loading) {
    const proxyServices = [
      // 注意：这些公共代理可能不稳定，建议配置自己的代理
      // `https://api.allorigins.win/raw?url=${encodeURIComponent(sourceUrl)}`,
      // `https://corsproxy.io/?${encodeURIComponent(sourceUrl)}`,
      // 使用简单的代理
      `https://cors-proxy.htmldriven.com/?url=${encodeURIComponent(sourceUrl)}`
    ];

    for (let proxyUrl of proxyServices) {
      try {
        this._updateProgressLoading(loading, `正在通过代理下载: ${new URL(proxyUrl).hostname}`, 50);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        const response = await fetch(proxyUrl, {
          signal: controller.signal,
          headers: {
            'Accept': '*/*',
            'User-Agent': 'Mozilla/5.0'
          }
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`代理返回 ${response.status}`);
        }

        const contentType = response.headers.get('content-type') || 'image/jpeg';
        const blob = await response.blob();

        this._updateProgressLoading(loading, '代理下载完成，准备上传', 60);

        const fileName = this._getFilenameFromUrl(sourceUrl);
        const file = new File([blob], fileName, {
          type: contentType,
          lastModified: Date.now()
        });

        loading.close();

        const uploadLoading = Loading.service({
          lock: true,
          text: '正在上传到OSS... 60.0%',
          background: 'rgba(0, 0, 0, 0.7)'
        });

        const result = await this.upload({
          file,
          filename: fileName,
          scene: destinationKey.split('/')[0],
          customParams: {
            ...customParams,
            'Content-Type': contentType
          },
          onProgress: (progress) => {
            const overallPercent = 60 + ((progress.percent || 0) * 0.4);
            this._updateProgressLoading(uploadLoading, '正在上传到OSS', overallPercent);
            this._safeProgressCallback(onProgress, {
              loaded: (blob.size || 0) + (progress.loaded || 0),
              total: (blob.size || 0) + (progress.total || 0),
              percent: Math.min(overallPercent, 100),
              stage: 'uploading'
            });
          }
        });

        this._updateProgressLoading(uploadLoading, '上传完成', 100);
        setTimeout(() => uploadLoading.close(), 500);

        return result;
      } catch (error) {
        console.warn(`代理 ${proxyUrl} 失败:`, error.message);
        continue;
      }
    }

    throw new Error('所有公共代理都失败');
  }

  // 通过服务器端代理下载
  async _downloadViaServerProxy(sourceUrl, destinationKey, customParams, onProgress, loading) {
    // 这里需要调用你自己的后端API
    // 示例：POST /api/proxy/download
    throw new Error('服务器端代理未配置，请联系后端开发人员');
  }

  // 添加绕过CORS的下载方法
  async _downloadWithCorsBypass(sourceUrl, destinationKey, customParams, onProgress, loading) {
    this._updateProgressLoading(loading, '正在绕过跨域限制下载文件', 15);

    try {
      // 方法1: 尝试通过服务器端代理
      this._updateProgressLoading(loading, '尝试方法1: 服务器代理', 20);
      try {
        return await this._downloadViaServerProxy(sourceUrl, destinationKey, customParams, onProgress, loading);
      } catch (proxyError) {
        console.warn('服务器代理失败:', proxyError.message);
      }

      // 方法2: 尝试公共CORS代理
      this._updateProgressLoading(loading, '尝试方法2: 公共代理', 30);
      try {
        return await this._downloadViaPublicProxy(sourceUrl, destinationKey, customParams, onProgress, loading);
      } catch (publicProxyError) {
        console.warn('公共代理失败:', publicProxyError.message);
      }

      // 方法3: 对于图片，使用canvas绘制
      if (this._isLikelyImageUrl(sourceUrl)) {
        this._updateProgressLoading(loading, '尝试方法3: Canvas绘制', 40);
        try {
          return await this._downloadImageViaCanvas(sourceUrl, destinationKey, customParams, onProgress, loading);
        } catch (canvasError) {
          console.warn('Canvas方法失败:', canvasError.message);
        }
      }

      throw new Error('所有绕过CORS的方法都失败');
    } catch (error) {
      throw error;
    }
  }

  // 通过Canvas绘制图片（仅限图片）
  async _downloadImageViaCanvas(sourceUrl, destinationKey, customParams, onProgress, loading) {
    return new Promise((resolve, reject) => {
      this._updateProgressLoading(loading, '正在通过Canvas绘制图片', 50);

      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const timeoutId = setTimeout(() => {
        reject(new Error('Canvas绘制超时'));
      }, 30000);

      img.crossOrigin = 'anonymous';

      img.onload = async () => {
        clearTimeout(timeoutId);

        try {
          this._updateProgressLoading(loading, '图片加载完成，正在绘制', 60);

          // 设置Canvas尺寸
          canvas.width = img.width;
          canvas.height = img.height;

          // 绘制图片
          ctx.drawImage(img, 0, 0);

          this._updateProgressLoading(loading, '绘制完成，正在转换格式', 70);

          // 转换为Blob
          canvas.toBlob(async (blob) => {
            if (!blob) {
              reject(new Error('Canvas转换Blob失败'));
              return;
            }

            this._updateProgressLoading(loading, '格式转换完成，准备上传', 80);

            const fileName = this._getFilenameFromUrl(sourceUrl) || 'image.png';
            const file = new File([blob], fileName, {
              type: 'image/png',
              lastModified: Date.now()
            });

            loading.close();

            const uploadLoading = Loading.service({
              lock: true,
              text: '正在上传到OSS... 80.0%',
              background: 'rgba(0, 0, 0, 0.7)'
            });

            try {
              const result = await this.upload({
                file,
                filename: fileName,
                scene: destinationKey.split('/')[0],
                customParams: {
                  ...customParams,
                  'Content-Type': 'image/png'
                },
                onProgress: (progress) => {
                  const overallPercent = 80 + ((progress.percent || 0) * 0.2);
                  this._updateProgressLoading(uploadLoading, '正在上传到OSS', overallPercent);
                  this._safeProgressCallback(onProgress, {
                    loaded: (blob.size || 0) + (progress.loaded || 0),
                    total: (blob.size || 0) + (progress.total || 0),
                    percent: Math.min(overallPercent, 100),
                    stage: 'uploading'
                  });
                }
              });

              this._updateProgressLoading(uploadLoading, '上传完成', 100);
              setTimeout(() => uploadLoading.close(), 500);

              resolve(result);
            } catch (uploadError) {
              uploadLoading.close();
              reject(uploadError);
            }
          }, 'image/png', 0.95); // 95%质量

        } catch (error) {
          clearTimeout(timeoutId);
          reject(error);
        }
      };

      img.onerror = () => {
        clearTimeout(timeoutId);
        reject(new Error('图片加载失败'));
      };

      // 添加时间戳避免缓存
      let imgSrc = sourceUrl;
      if (imgSrc.indexOf('?') === -1) {
        imgSrc += '?_=' + Date.now();
      } else {
        imgSrc += '&_=' + Date.now();
      }

      img.src = imgSrc;
    });
  }

  // 添加通过代理或直接下载的方法
  async _downloadWithProxyOrDirect(sourceUrl, destinationKey, customParams, onProgress, loading) {
    loading.text = '正在尝试下载文件（可能需要绕过限制）...';

    this._safeProgressCallback(onProgress, {
      loaded: 0,
      total: 1,
      percent: 0,
      stage: 'preparing'
    });

    try {
      // 尝试通过CORS代理
      const proxyUrl = await this._getCorsProxyUrl(sourceUrl);

      this._safeProgressCallback(onProgress, {
        loaded: 1,
        total: 10,
        percent: 10,
        stage: 'downloading'
      });

      const response = await fetch(proxyUrl, {
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          'Accept': '*/*',
          'Referer': 'https://example.com/'
        }
      });

      if (!response.ok) {
        throw new Error(`代理下载失败: ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || await this._getMimeTypeFromUrl(sourceUrl);
      const blob = await response.blob();

      this._safeProgressCallback(onProgress, {
        loaded: blob.size || 0,
        total: blob.size || 1,
        percent: 50,
        stage: 'uploading'
      });

      loading.text = '文件下载完成，正在上传到OSS...';

      const fileName = this._getFilenameFromUrl(sourceUrl);
      const file = new File([blob], fileName, {
        type: contentType,
        lastModified: Date.now()
      });

      loading.close();

      const uploadLoading = Loading.service({
        lock: true,
        text: '正在上传到OSS...',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      const result = await this.upload({
        file,
        filename: fileName,
        scene: destinationKey.split('/')[0],
        customParams: {
          ...customParams,
          'Content-Type': contentType
        },
        onProgress: (progress) => {
          const overallPercent = 50 + ((progress.percent || 0) * 0.5);
          this._safeProgressCallback(onProgress, {
            loaded: (blob.size || 0) + (progress.loaded || 0),
            total: (blob.size || 0) + (progress.total || 0),
            percent: Math.min(overallPercent, 100),
            stage: 'uploading'
          });
        }
      });

      uploadLoading.close();
      return result;
    } catch (proxyError) {
      console.warn('代理下载失败，尝试直接下载:', proxyError);

      // 尝试直接下载
      return await this._downloadDirectBypass(sourceUrl, destinationKey, customParams, onProgress, loading);
    }
  }

  // 获取CORS代理URL
  async _getCorsProxyUrl(url) {
    // 这里可以配置多个CORS代理服务
    const proxyServices = [
      // 公共CORS代理（注意：这些服务可能不稳定或有限制）
      `https://cors-anywhere.herokuapp.com/${url}`,
      `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
      `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`,
      // 可以添加自己的代理服务
    ];

    // 返回第一个可用的代理
    return proxyServices[0];
  }

  // 直接下载绕过限制
  async _downloadDirectBypass(sourceUrl, destinationKey, customParams, onProgress, loading) {
    loading.text = '正在尝试直接下载文件...';

    try {
      // 创建iframe下载
      return new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = sourceUrl;
        document.body.appendChild(iframe);

        // 设置超时
        const timeoutId = setTimeout(() => {
          document.body.removeChild(iframe);
          reject(new Error('下载超时'));
        }, 30000);

        iframe.onload = async () => {
          clearTimeout(timeoutId);

          try {
            // 尝试从iframe获取内容
            const blob = await this._getBlobFromIframe(iframe);

            if (!blob) {
              document.body.removeChild(iframe);
              reject(new Error('无法获取文件内容'));
              return;
            }

            document.body.removeChild(iframe);

            const contentType = blob.type || await this._getMimeTypeFromUrl(sourceUrl);
            const fileName = this._getFilenameFromUrl(sourceUrl);
            const file = new File([blob], fileName, {
              type: contentType,
              lastModified: Date.now()
            });

            this._safeProgressCallback(onProgress, {
              loaded: blob.size || 0,
              total: blob.size || 1,
              percent: 50,
              stage: 'uploading'
            });

            loading.text = '文件下载完成，正在上传到OSS...';
            loading.close();

            const uploadLoading = Loading.service({
              lock: true,
              text: '正在上传到OSS...',
              background: 'rgba(0, 0, 0, 0.7)'
            });

            const result = await this.upload({
              file,
              filename: fileName,
              scene: destinationKey.split('/')[0],
              customParams: {
                ...customParams,
                'Content-Type': contentType
              },
              onProgress: (progress) => {
                const overallPercent = 50 + ((progress.percent || 0) * 0.5);
                this._safeProgressCallback(onProgress, {
                  loaded: (blob.size || 0) + (progress.loaded || 0),
                  total: (blob.size || 0) + (progress.total || 0),
                  percent: Math.min(overallPercent, 100),
                  stage: 'uploading'
                });
              }
            });

            uploadLoading.close();
            resolve(result);
          } catch (error) {
            document.body.removeChild(iframe);
            reject(error);
          }
        };

        iframe.onerror = () => {
          clearTimeout(timeoutId);
          document.body.removeChild(iframe);
          reject(new Error('iframe加载失败'));
        };
      });
    } catch (error) {
      throw new Error(`直接下载失败: ${error.message}`);
    }
  }

  // 从iframe获取Blob
  _getBlobFromIframe(iframe) {
    return new Promise((resolve) => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        const img = doc.querySelector('img');

        if (img && img.src) {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/png');
        } else {
          resolve(null);
        }
      } catch (e) {
        resolve(null);
      }
    });
  }


  // 针对图片的特殊处理
  async _downloadAndUploadImage(sourceUrl, destinationKey, contentType, customParams, onProgress, loading) {
    try {
      this._updateProgressLoading(loading, '开始下载图片', 0);
      this._safeProgressCallback(onProgress, {
        loaded: 0,
        total: 1,
        percent: 0,
        stage: 'downloading'
      });

      const response = await fetch(sourceUrl);
      if (!response.ok) {
        throw new Error(`下载图片失败: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      this._updateProgressLoading(loading, '图片下载完成，正在准备上传', 50);
      this._safeProgressCallback(onProgress, {
        loaded: blob.size || 0,
        total: blob.size || 1,
        percent: 50,
        stage: 'uploading'
      });

      const fileName = this._getFilenameFromUrl(sourceUrl);
      const file = new File([blob], fileName, {
        type: contentType,
        lastModified: Date.now()
      });

      loading.close();

      const uploadLoading = Loading.service({
        lock: true,
        text: '正在上传到OSS... 50.0%',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      const result = await this.upload({
        file,
        filename: fileName,
        scene: destinationKey.split('/')[0],
        customParams: {
          ...customParams,
          'Content-Type': contentType
        },
        onProgress: (progress) => {
          const overallPercent = 50 + ((progress.percent || 0) * 0.5);
          this._updateProgressLoading(uploadLoading, '正在上传到OSS', overallPercent);
          this._safeProgressCallback(onProgress, {
            loaded: (blob.size || 0) + (progress.loaded || 0),
            total: (blob.size || 0) + (progress.total || 0),
            percent: Math.min(overallPercent, 100),
            stage: 'uploading'
          });
        }
      });

      this._updateProgressLoading(uploadLoading, '上传完成', 100);
      this._safeProgressCallback(onProgress, {
        loaded: (blob.size || 0) + (file.size || 0),
        total: (blob.size || 0) + (file.size || 0),
        percent: 100,
        stage: 'complete'
      });

      setTimeout(() => {
        uploadLoading.close();
      }, 500);

      return result;
    } catch (error) {
      throw error;
    }
  }


  // 添加获取文件的替代方法
  async _getRemoteFileInfoWithRetry(sourceUrl, retryCount = 0) {
    const maxRetries = 3;
    const methods = ['GET', 'HEAD']; // 交换顺序，先尝试GET

    for (let method of methods) {
      let timeoutId;
      try {
        const controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), 10000); // 减少超时时间

        // 简化请求头，避免触发CORS预检
        const headers = {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          'Accept': '*/*'
        };

        // 对于这个特定域名，添加必要的请求头
        if (sourceUrl.includes('ys7.com')) {
          headers['Origin'] = 'https://www.ys7.com';
          headers['Referer'] = 'https://www.ys7.com/';
        }

        const response = await fetch(sourceUrl, {
          method: method,
          headers: headers,
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok || response.status === 200 || response.status === 206) {
          const contentType = response.headers.get('content-type');
          const contentLength = parseInt(response.headers.get('content-length') || '0');
          const acceptRanges = response.headers.get('accept-ranges');
          const contentDisposition = response.headers.get('content-disposition');
          const lastModified = response.headers.get('last-modified');
          const etag = response.headers.get('etag');

          return {
            valid: true,
            contentType,
            contentLength,
            supportsRange: acceptRanges === 'bytes',
            contentDisposition,
            lastModified,
            etag,
            status: response.status
          };
        } else if (response.status === 403 || response.status === 401 || response.status === 405) {
          // 405表示方法不允许
          console.warn(`${method}请求返回${response.status}，尝试其他方法...`);
          continue;
        } else {
          return {
            valid: false,
            error: `HTTP ${response.status}: ${response.statusText}`
          };
        }
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);
        console.warn(`${method}请求失败:`, error.message);

        if (error.name === 'AbortError') {
          console.warn('请求超时，继续尝试其他方法');
          continue;
        }

        if (retryCount < maxRetries && !error.message.includes('CORS') && !error.message.includes('Failed to fetch')) {
          console.warn(`重试中... (${retryCount + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          continue;
        }

        // 如果是CORS错误，直接返回
        if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
          return {
            valid: false,
            error: 'CORS错误: 服务器不允许跨域访问',
            corsError: true
          };
        }
      }
    }

    return {
      valid: false,
      error: '所有请求方法都失败',
      corsError: true
    };
  }

  // 添加不触发CORS预检的获取方法
  async _getFileInfoNoCors(sourceUrl) {
    return new Promise((resolve) => {
      // 对于图片，可以使用Image对象
      if (this._isLikelyImageUrl(sourceUrl) || sourceUrl.includes('image') || sourceUrl.includes('ys7.com')) {
        const img = new Image();

        const timeoutId = setTimeout(() => {
          img.onload = null;
          img.onerror = null;
          resolve({
            valid: false,
            error: '图片加载超时',
            corsError: true
          });
        }, 10000);

        img.onload = function() {
          clearTimeout(timeoutId);
          resolve({
            valid: true,
            contentType: 'image/jpeg', // 假设是JPEG
            contentLength: 0,
            supportsRange: false,
            isImage: true,
            corsError: false
          });
        };

        img.onerror = function() {
          clearTimeout(timeoutId);
          resolve({
            valid: false,
            error: '图片加载失败',
            corsError: true
          });
        };

        // 尝试不同的加载方式
        img.crossOrigin = 'anonymous';
        img.src = sourceUrl;
      } else {
        // 非图片文件，尝试通过script标签
        resolve({
          valid: false,
          error: '非图片文件，无法绕过CORS',
          corsError: true
        });
      }
    });
  }

  // 获取远程文件信息
  async _getRemoteFileInfo(sourceUrl) {
    // 先尝试普通方法
    const result = await this._getRemoteFileInfoWithRetry(sourceUrl);

    if (result.valid) {
      return result;
    }

    // 如果有CORS错误，尝试不触发CORS的方法
    if (result.corsError) {
      console.log('检测到CORS错误，尝试绕过CORS...');
      return await this._getFileInfoNoCors(sourceUrl);
    }

    return result;
  }

  // 判断URL是否可能是图片
  async _isLikelyImageUrl(url) {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    return imageExtensions.some(ext => url.toLowerCase().includes(ext));
  }

  // 通过img标签获取图片信息
  async _getImageInfoViaImgTag(sourceUrl) {
    return new Promise((resolve) => {
      const img = new Image();

      // 设置超时
      const timeoutId = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        resolve({
          valid: false,
          error: '图片加载超时'
        });
      }, 10000);

      img.onload = () => {
        clearTimeout(timeoutId);
        resolve({
          valid: true,
          contentType: this._getContentTypeByExtension(sourceUrl.split('.').pop()),
          contentLength: 0, // 无法获取大小
          supportsRange: false,
          isImage: true,
          width: img.width,
          height: img.height
        });
      };

      img.onerror = () => {
        clearTimeout(timeoutId);
        resolve({
          valid: false,
          error: '图片加载失败'
        });
      };

      // 设置crossOrigin属性
      img.crossOrigin = 'anonymous';
      img.src = sourceUrl;

      // 添加时间戳避免缓存
      if (img.src.indexOf('?') === -1) {
        img.src += '?t=' + Date.now();
      } else {
        img.src += '&t=' + Date.now();
      }
    });
  }

  // 在类中添加一个安全的进度回调包装器
  _safeProgressCallback(callback, data) {
    if (!callback || typeof callback !== 'function') {
      return;
    }

    try {
      // 确保所有必需的属性都有默认值
      const safeData = {
        loaded: data.loaded || 0,
        total: data.total || 1,
        percent: typeof data.percent === 'number' ? data.percent : 0,
        stage: data.stage || 'processing',
        ...data
      };

      // 确保percent在0-100范围内
      if (typeof safeData.percent === 'number') {
        safeData.percent = Math.max(0, Math.min(100, safeData.percent));
      } else {
        safeData.percent = 0;
      }

      callback(safeData);
    } catch (error) {
      console.warn('进度回调执行出错，已忽略:', error.message);
    }
  }

  // 支持Range的下载（用于大文件）
  async _downloadWithRange(sourceUrl, destinationKey, totalSize, contentType, customParams, onProgress, loading) {
    const chunkSize = 5 * 1024 * 1024;
    const chunkCount = Math.ceil(totalSize / chunkSize);
    const chunks = [];
    let downloadedSize = 0;

    // 初始进度
    this._updateProgressLoading(loading, '开始下载文件', 0);
    this._safeProgressCallback(onProgress, {
      loaded: 0,
      total: totalSize,
      percent: 0,
      stage: 'downloading'
    });

    // 创建下载任务
    const downloadTasks = [];
    for (let i = 0; i < chunkCount; i++) {
      downloadTasks.push(async () => {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize - 1, totalSize - 1);

        this._updateProgressLoading(loading, '正在下载分片', (downloadedSize / totalSize) * 50);

        const response = await fetch(sourceUrl, {
          headers: { 'Range': `bytes=${start}-${end}` }
        });

        if (!response.ok) {
          throw new Error(`下载分片失败: HTTP ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        chunks[i] = arrayBuffer;
        downloadedSize += arrayBuffer.byteLength;

        // 更新进度
        const percent = totalSize > 0 ? (downloadedSize / totalSize) * 50 : 0;
        this._updateProgressLoading(loading, '正在下载文件', percent);
        this._safeProgressCallback(onProgress, {
          loaded: downloadedSize,
          total: totalSize,
          percent: Math.min(percent, 50),
          stage: 'downloading'
        });
      });
    }

    // 并行下载
    await this._controlledParallelDownload(downloadTasks, 3);

    // 下载完成
    this._updateProgressLoading(loading, '文件下载完成，正在准备上传', 50);
    this._safeProgressCallback(onProgress, {
      loaded: totalSize,
      total: totalSize,
      percent: 50,
      stage: 'uploading'
    });

    // 合并chunks
    const blob = new Blob(chunks, { type: contentType });
    const fileName = this._getFilenameFromUrl(sourceUrl);
    const file = new File([blob], fileName, {
      type: contentType,
      lastModified: Date.now()
    });

    loading.close();

    // 上传到OSS
    const uploadLoading = Loading.service({
      lock: true,
      text: '正在上传到OSS... 50.0%',
      background: 'rgba(0, 0, 0, 0.7)'
    });

    try {
      const result = await this.upload({
        file,
        filename: fileName,
        scene: destinationKey.split('/')[0],
        customParams: {
          ...customParams,
          'Content-Type': contentType
        },
        onProgress: (progress) => {
          // 整体进度计算：下载50% + 上传50%
          const uploadPercent = progress.percent || 0;
          const overallPercent = 50 + (uploadPercent * 0.5);
          this._updateProgressLoading(uploadLoading, '正在上传到OSS', overallPercent);
          this._safeProgressCallback(onProgress, {
            loaded: totalSize + (progress.loaded || 0),
            total: totalSize + (progress.total || 0),
            percent: Math.min(overallPercent, 100),
            stage: 'uploading'
          });
        }
      });

      // 上传完成
      this._updateProgressLoading(uploadLoading, '上传完成', 100);
      this._safeProgressCallback(onProgress, {
        loaded: totalSize + (file.size || 0),
        total: totalSize + (file.size || 0),
        percent: 100,
        stage: 'complete'
      });

      // 延迟关闭，让用户看到100%
      setTimeout(() => {
        uploadLoading.close();
      }, 500);

      return result;
    } catch (error) {
      uploadLoading.close();
      throw error;
    }
  }

  // 直接下载并上传
  async _downloadAndUploadDirect(sourceUrl, destinationKey, contentType, customParams, onProgress, loading, totalSize) {
    this._updateProgressLoading(loading, '开始下载文件', 0);
    this._safeProgressCallback(onProgress, {
      loaded: 0,
      total: totalSize || 0,
      percent: 0,
      stage: 'downloading'
    });

    try {
      const response = await fetch(sourceUrl);
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status} ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const chunks = [];
      let receivedSize = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        receivedSize += value.length;

        if (totalSize > 0) {
          const percent = (receivedSize / totalSize) * 50;
          this._updateProgressLoading(loading, '正在下载文件', percent);
          this._safeProgressCallback(onProgress, {
            loaded: receivedSize,
            total: totalSize,
            percent: Math.min(percent, 50),
            stage: 'downloading'
          });
        } else {
          this._updateProgressLoading(loading, '正在下载文件');
          this._safeProgressCallback(onProgress, {
            loaded: receivedSize,
            total: 0,
            percent: 0,
            stage: 'downloading'
          });
        }
      }

      this._updateProgressLoading(loading, '文件下载完成，正在准备上传', 50);
      this._safeProgressCallback(onProgress, {
        loaded: totalSize || receivedSize,
        total: totalSize || receivedSize,
        percent: 50,
        stage: 'uploading'
      });

      const blob = new Blob(chunks, { type: contentType });
      const fileName = this._getFilenameFromUrl(sourceUrl);
      const file = new File([blob], fileName, {
        type: contentType,
        lastModified: Date.now()
      });

      loading.close();

      const uploadLoading = Loading.service({
        lock: true,
        text: '正在上传到OSS... 50.0%',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      const result = await this.upload({
        file,
        filename: fileName,
        scene: destinationKey.split('/')[0],
        customParams: {
          ...customParams,
          'Content-Type': contentType
        },
        onProgress: (progress) => {
          const uploadPercent = progress.percent || 0;
          const overallPercent = 50 + (uploadPercent * 0.5);
          this._updateProgressLoading(uploadLoading, '正在上传到OSS', overallPercent);
          this._safeProgressCallback(onProgress, {
            loaded: (totalSize || 0) + (progress.loaded || 0),
            total: (totalSize || 0) + (progress.total || 0),
            percent: Math.min(overallPercent, 100),
            stage: 'uploading'
          });
        }
      });

      this._updateProgressLoading(uploadLoading, '上传完成', 100);
      this._safeProgressCallback(onProgress, {
        loaded: (totalSize || 0) + (file.size || 0),
        total: (totalSize || 0) + (file.size || 0),
        percent: 100,
        stage: 'complete'
      });

      setTimeout(() => {
        uploadLoading.close();
      }, 500);

      return result;
    } catch (error) {
      loading.close();
      throw error;
    }
  }


  // 并行下载控制
  async _controlledParallelDownload(tasks, maxParallel = 3) {
    const results = [];
    const executing = new Set();

    for (const task of tasks) {
      if (executing.size >= maxParallel) {
        await Promise.race(executing);
      }

      const promise = task().finally(() => {
        executing.delete(promise);
      });

      executing.add(promise);
      results.push(promise);
    }

    return Promise.all(results);
  }

  // 检查URL是否支持CORS
  async _checkCORS(url) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      return {
        supported: true,
        status: response.status
      };
    } catch (error) {
      return {
        supported: false,
        error: error.message
      };
    }
  }

  // 批量复制多个URL
  async copyMultipleFromUrls({
    urls = [],
    scene = 'default',
    customParams = {},
    onProgress = () => {},
    onFileComplete = () => {},
    onAllComplete = () => {}
  }) {
    if (!Array.isArray(urls) || urls.length === 0) {
      throw new Error('请输入URL数组');
    }

    const results = [];
    const totalFiles = urls.length;

    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      try {
        const fileUrl = await this.copyFromUrl({
          sourceUrl: url,
          scene,
          customParams,
          onProgress: (progress) => {
            // 计算整体进度
            const fileProgress = progress.percent || 0;
            const overallPercent = (i / totalFiles * 100) + (fileProgress / totalFiles);
            onProgress({
              current: i + 1,
              total: totalFiles,
              fileProgress,
              overallPercent
            });
          }
        });

        results.push({
          url,
          success: true,
          fileUrl
        });

        onFileComplete({
          index: i,
          total: totalFiles,
          url,
          fileUrl,
          success: true
        });
      } catch (error) {
        console.error(`复制文件失败: ${url}`, error);
        results.push({
          url,
          success: false,
          error: error.message
        });

        onFileComplete({
          index: i,
          total: totalFiles,
          url,
          success: false,
          error: error.message
        });
      }
    }

    onAllComplete(results);
    return results;
  }

  // 取消上传（基于 AbortController，ali-oss multipartUpload 通过 signal 取消）
  cancelUpload() {
    if (this._abortController) {
      try {
        this._abortController.abort();
      } catch (e) {
        console.warn('取消上传时出错:', e);
      }
      this._abortController = null;
    }
    this.isUploading = false;
  }

  // 裸URL换签名URL（私有bucket下，上传成功后的即时预览必须用签名URL）
  // 后端 /v1/upload/signUrl 按输入顺序返回签名URL数组
  async _getSignedUrl(rawUrl) {
    const token = local.get('token');
    const brand = local.get('brand');
    const response = await axios.post(filterUrl('/v1/upload/signUrl'), { urls: [rawUrl] }, {
      headers: {
        'Authorization': token,
        'Brand': brand,
        'Platform': 'pc',
        'Content-Type': 'application/json'
      },
      timeout: 10000
    });
    const body = response && response.data;
    if (body && body.status === 1 && Array.isArray(body.data) && body.data[0]) {
      return body.data[0];
    }
    throw new Error((body && body.message) || '获取签名URL失败');
  }

  // 批量裸URL换签名URL（私有bucket，启动预签/图片过期重签复用）
  // 与 _getSignedUrl 同源，但支持一次传入多个 URL，按输入顺序返回签名URL数组
  async getSignedUrls(rawUrls) {
    if (!Array.isArray(rawUrls) || rawUrls.length === 0) return [];
    const token = local.get('token');
    const brand = local.get('brand');
    const response = await axios.post(filterUrl('/v1/upload/signUrl'), { urls: rawUrls }, {
      headers: {
        'Authorization': token,
        'Brand': brand,
        'Platform': 'pc',
        'Content-Type': 'application/json'
      },
      timeout: 10000
    });
    const body = response && response.data;
    if (body && body.status === 1 && Array.isArray(body.data)) {
      return body.data;
    }
    throw new Error((body && body.message) || '获取签名URL失败');
  }

  // 统一上传方法（对外签名保持不变，内部改为 STS + ali-oss SDK）
  // - 文件上传：走 ali-oss multipartUpload（SDK 自动判断是否分片，自带断点续传/并发/重试）
  // - URL 转存：转给 copyFromUrl
  // - 私有 bucket 下，自动过滤 customParams 中的 x-oss-object-acl
  // - STS 凭证 30 分钟过期，捕获 403 后自动重新取凭证重试 1 次
  // - 上传成功后自动换取签名URL：onComplete(url, previewUrl)，
  //   返回值为字符串对象（原值=裸URL入库用），.previewUrl / .url 属性按需取用
  async upload({
    file,
    sourceUrl,
    filename = '',
    scene = 'default',
    customParams = {},
    chunkSize = this.config.defaultChunkSize,
    multipartThreshold = this.config.defaultMultipartThreshold,
    onProgress = () => {},
    onComplete = () => {}
  }) {
    // URL 转存：交给 copyFromUrl
    if (sourceUrl) {
      return this.copyFromUrl({
        sourceUrl,
        filename,
        scene,
        customParams,
        onProgress: (progress) => {
          this._safeProgressCallback(onProgress, progress);
        },
        onComplete
      });
    }

    if (!file) {
      throw new Error('请选择文件或提供URL');
    }

    const fileType = this._getFileType(file);
    const filePath = this._generateFilePath(file, filename, scene, fileType);

    // 从 filePath 中提取目录前缀，用于 STS 凭证请求（后端按目录授权）
    // filePath 形如 "images/jpg/20260818/xxx.jpg" → dir = "images/jpg/20260818/"
    const dir = filePath.split('/').slice(0, 3).join('/') + '/';

    this.isUploading = true;

    const uploadLoading = Loading.service({
      lock: true,
      text: '正在上传文件... 0.0%',
      background: 'rgba(0, 0, 0, 0.7)'
    });

    try {
      const safeOnProgress = (progress) => {
        this._updateProgressLoading(uploadLoading, '正在上传文件', progress.percent);
        this._safeProgressCallback(onProgress, progress);
      };

      // multipartThreshold 仅用于决定 partSize 的下限（ali-oss 内部会自动判断是否分片）
      // 这里统一走 multipartUpload：对小于 partSize 的文件 SDK 会自动用 PutObject
      const partSize = Math.max(chunkSize || this.config.defaultChunkSize, 1 * 1024 * 1024);

      await this._uploadWithSTS({
        file,
        key: filePath,
        dir,
        customParams,
        chunkSize: partSize,
        onProgress: safeOnProgress
      });

      // 拼接入库 URL（与改造前保持一致：customDomain + filePath）
      const fileUrl = `${this.config.customDomain}/${filePath}`;

      // 私有 bucket：页面即时预览必须用签名 URL，裸 URL 会 403。
      // 入库仍然存裸 URL（fileUrl），列表/详情接口返回时由后端中间件统一签名。
      let previewUrl = fileUrl;
      try {
        previewUrl = await this._getSignedUrl(fileUrl);
      } catch (e) {
        console.warn('获取预览签名URL失败，降级使用裸URL:', e.message);
      }

      this._updateProgressLoading(uploadLoading, '上传完成', 100);
      setTimeout(() => {
        uploadLoading.close();
      }, 500);

      // onComplete(url, previewUrl)：第一个参数保持裸URL向后兼容，
      // 需要即时展示的调用方请用第二个参数 previewUrl
      onComplete(fileUrl, previewUrl);
      // 返回值兼容处理：返回字符串对象，主值是裸URL，同时挂载 previewUrl 属性。
      // 老代码 `const url = await upload()` 拿到的仍是裸URL字符串（用于入库），
      // 需要展示时改用 `res.previewUrl` 或 onComplete 的第二个参数。
      const result = new String(fileUrl);
      result.previewUrl = previewUrl;
      result.url = fileUrl;
      return result;
    } catch (error) {
      uploadLoading.close();
      // 用户取消时不要抛出错误堆栈
      if (error && error.message === '上传已取消') {
        throw error;
      }
      console.error('上传失败:', error);
      throw error;
    } finally {
      this.isUploading = false;
      this._abortController = null;
    }
  }
}

// 导出单例
export default new OSSUploader();
